import { useToast } from '@monorepo/shared/contexts';
import { FormTemplateResponse } from 'mapistry-shared';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFormTemplateGeneratedMessageHandlers } from '../../../../../vortex/src/contexts/pushMessages/pushMessageHandlers/useFormTemplateGeneratedMessageHandlers';
import { useFormTemplateContext } from '../../EditFormTemplatePage/FormTemplateContext';
import { useStartGeneratingFormTemplate } from './useStartGeneratingFormTemplate';

export function useGenerateFormTemplateAndListenForPushMessageResponse({
  organizationId,
}: {
  organizationId?: string;
}) {
  const { showUserFriendlyErrorToast, error: showErrorToast } = useToast();
  const {
    formTemplateHelpers: { replaceAllGroupsAndFields },
  } = useFormTemplateContext();

  const [tabIdentifier, setTabIdentifier] = useState<string>();
  const [startedProcessingAt, setStartedProcessingAt] = useState<Date>();
  const isProcessing = !!startedProcessingAt;

  const [startGeneratingFormTemplate] = useStartGeneratingFormTemplate({
    config: {
      onError: (e) => {
        setStartedProcessingAt(undefined);
        setTabIdentifier(undefined);
        showUserFriendlyErrorToast(e, undefined, { dontAutoHide: true });
      },
    },
  });

  const handleUpload = useCallback(
    ({ file, customPrompt }: { file?: File; customPrompt?: string }) => {
      if (!file && !customPrompt) {
        showErrorToast('Upload a file and/or enter a custom prompt.');
        return;
      }

      const uniqueId = uuidv4();
      setStartedProcessingAt(new Date());
      setTabIdentifier(uniqueId);
      void startGeneratingFormTemplate({
        file,
        customPrompt,
        tabIdentifier: uniqueId,
        organizationId,
      });
    },
    [startGeneratingFormTemplate, showErrorToast, organizationId],
  );

  const onFinishedGeneratingFormTemplate = useCallback(
    ({
      formTemplate: generatedFormTemplate,
      error: errorMessage,
      tabIdentifier: tabIdentifierFromMessage,
    }: {
      formTemplate?: FormTemplateResponse;
      error?: string;
      tabIdentifier: string;
    }) => {
      if (tabIdentifierFromMessage !== tabIdentifier) return;

      setStartedProcessingAt(undefined);
      setTabIdentifier(undefined);

      if (generatedFormTemplate) {
        replaceAllGroupsAndFields(generatedFormTemplate.groups);
      }
      if (errorMessage) {
        // eslint-disable-next-line no-console
        console.log('Form template creation error', errorMessage);
        showErrorToast(
          `Something went wrong processing that request. Try again. ${errorMessage} `,
          { dontAutoHide: true },
        );
      }
    },
    [showErrorToast, tabIdentifier, replaceAllGroupsAndFields],
  );

  // start listening to push notifications for form template generation, and pass in callback on success
  useFormTemplateGeneratedMessageHandlers(onFinishedGeneratingFormTemplate);

  return { isProcessing, handleUpload, startedProcessingAt };
}
